// src/components/AIAgent/ConversationDetail.jsx

import React, { useEffect, useState } from "react";
import { useAIAgentStore } from "../../services/useAIAgentStore";
import "./AIAgent.css";

const ConversationDetail = ({ conversationId, onClose }) => {
  const {
    currentConversation,
    conversationStats,
    conversationSummaries,
    loading,
    error,
    fetchConversation,
    fetchConversationStats,
    fetchConversationSummaries,
    generateSummary,
    escalateConversation,
    clearError,
  } = useAIAgentStore();

  const [activeTab, setActiveTab] = useState("messages");
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateUserId, setEscalateUserId] = useState("");
  const [escalateReason, setEscalateReason] = useState("");
  const [generatingSummary, setGeneratingSummary] = useState(false);

  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId);
      fetchConversationStats(conversationId);
      fetchConversationSummaries(conversationId);
    }
    return () => clearError();
  }, [conversationId]);

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      await generateSummary(conversationId, false);
      await fetchConversationStats(conversationId);
      await fetchConversationSummaries(conversationId);
    } catch (err) {
      console.error("Error generating summary:", err);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalateUserId) return;
    try {
      await escalateConversation(conversationId, parseInt(escalateUserId), escalateReason);
      setShowEscalateModal(false);
      fetchConversation(conversationId);
    } catch (err) {
      console.error("Error escalating:", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTokens = (tokens) => {
    if (!tokens) return "0";
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return tokens.toString();
  };

  if (loading && !currentConversation) {
    return (
      <div className="conversation-detail">
        <div className="conversation-loading">Carregando...</div>
      </div>
    );
  }

  if (!currentConversation) {
    return (
      <div className="conversation-detail">
        <div className="conversation-not-found">Conversa nao encontrada</div>
      </div>
    );
  }

  const { conversation, messages } = currentConversation;

  return (
    <div className="conversation-detail">
      {/* Header */}
      <div className="conversation-header">
        <div className="conversation-header-info">
          <h3>Conversa #{conversationId?.slice(0, 8)}</h3>
          <span className={`conversation-status status-${conversation.status}`}>
            {conversation.status}
          </span>
        </div>
        <button className="btn-close" onClick={onClose}>
          X
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Bar */}
      {conversationStats && (
        <div className="conversation-stats-bar">
          <div className="stat-item">
            <span className="stat-label">Mensagens</span>
            <span className="stat-value">{conversationStats.message_count || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Tokens (Usuario)</span>
            <span className="stat-value">{formatTokens(conversationStats.user_tokens)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Tokens (IA)</span>
            <span className="stat-value">{formatTokens(conversationStats.assistant_tokens)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Tokens Salvos</span>
            <span className="stat-value highlight">{formatTokens(conversationStats.total_tokens_saved)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Resumos</span>
            <span className="stat-value">{conversationStats.summary_count || 0}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="conversation-tabs">
        <button
          className={`tab-btn ${activeTab === "messages" ? "active" : ""}`}
          onClick={() => setActiveTab("messages")}
        >
          Mensagens
        </button>
        <button
          className={`tab-btn ${activeTab === "summaries" ? "active" : ""}`}
          onClick={() => setActiveTab("summaries")}
        >
          Resumos
        </button>
        <button
          className={`tab-btn ${activeTab === "info" ? "active" : ""}`}
          onClick={() => setActiveTab("info")}
        >
          Informacoes
        </button>
      </div>

      {/* Tab Content */}
      <div className="conversation-tab-content">
        {/* Messages Tab */}
        {activeTab === "messages" && (
          <div className="messages-container">
            {messages && messages.length > 0 ? (
              messages.map((msg) => (
                <div key={msg.id} className={`message-item role-${msg.role}`}>
                  <div className="message-header">
                    <span className="message-role">
                      {msg.role === "user" ? "Usuario" : "IA"}
                    </span>
                    <span className="message-time">{formatDate(msg.created_at)}</span>
                  </div>
                  <div className="message-content">{msg.content}</div>
                  {msg.role === "assistant" && msg.total_tokens && (
                    <div className="message-meta">
                      <span>{msg.total_tokens} tokens</span>
                      {msg.latency_ms && <span>{msg.latency_ms}ms</span>}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-messages">Nenhuma mensagem</div>
            )}
          </div>
        )}

        {/* Summaries Tab */}
        {activeTab === "summaries" && (
          <div className="summaries-container">
            <div className="summaries-header">
              <h4>Resumos da Conversa</h4>
              <button
                className="btn-generate-summary"
                onClick={handleGenerateSummary}
                disabled={generatingSummary}
              >
                {generatingSummary ? "Gerando..." : "Gerar Resumo"}
              </button>
            </div>

            {/* Current Summary */}
            {conversation.current_summary && (
              <div className="current-summary">
                <h5>Resumo Atual</h5>
                <p>{conversation.current_summary}</p>
              </div>
            )}

            {/* Summary History */}
            <div className="summary-history">
              <h5>Historico de Resumos</h5>
              {conversationSummaries && conversationSummaries.length > 0 ? (
                conversationSummaries.map((summary) => (
                  <div key={summary.id} className="summary-item">
                    <div className="summary-meta">
                      <span>{formatDate(summary.created_at)}</span>
                      <span>{summary.messages_summarized} msgs</span>
                      <span className="tokens-saved">
                        {formatTokens(summary.tokens_saved)} tokens salvos
                      </span>
                    </div>
                    <div className="summary-text">{summary.summary_text}</div>
                  </div>
                ))
              ) : (
                <div className="no-summaries">Nenhum resumo gerado ainda</div>
              )}
            </div>
          </div>
        )}

        {/* Info Tab */}
        {activeTab === "info" && (
          <div className="info-container">
            <div className="info-grid">
              <div className="info-item">
                <label>ID</label>
                <span>{conversation.id}</span>
              </div>
              <div className="info-item">
                <label>Lead ID</label>
                <span>{conversation.lead_id}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span className={`status-badge status-${conversation.status}`}>
                  {conversation.status}
                </span>
              </div>
              <div className="info-item">
                <label>Criada em</label>
                <span>{formatDate(conversation.created_at)}</span>
              </div>
              <div className="info-item">
                <label>Ultima mensagem</label>
                <span>{formatDate(conversation.last_message_at)}</span>
              </div>
              <div className="info-item">
                <label>Ultimo resumo</label>
                <span>{formatDate(conversationStats?.last_summary_at)}</span>
              </div>
              {conversation.escalated_to && (
                <div className="info-item">
                  <label>Escalada para</label>
                  <span>Usuario #{conversation.escalated_to}</span>
                </div>
              )}
              {conversation.escalation_reason && (
                <div className="info-item full-width">
                  <label>Motivo da escalacao</label>
                  <span>{conversation.escalation_reason}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            {conversation.status === "active" && (
              <div className="info-actions">
                <button
                  className="btn-escalate"
                  onClick={() => setShowEscalateModal(true)}
                >
                  Escalar para Humano
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Escalate Modal */}
      {showEscalateModal && (
        <div className="modal-overlay" onClick={() => setShowEscalateModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Escalar Conversa</h3>
              <button
                className="btn-close"
                onClick={() => setShowEscalateModal(false)}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>ID do Usuario</label>
                <input
                  type="number"
                  value={escalateUserId}
                  onChange={(e) => setEscalateUserId(e.target.value)}
                  placeholder="ID do usuario para escalar"
                />
              </div>
              <div className="form-group">
                <label>Motivo</label>
                <textarea
                  value={escalateReason}
                  onChange={(e) => setEscalateReason(e.target.value)}
                  placeholder="Motivo da escalacao..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowEscalateModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleEscalate}
                disabled={!escalateUserId}
              >
                Escalar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationDetail;
